import { OverlayContainer } from "@angular/cdk/overlay";
import {
  HttpBackend,
  provideHttpClient,
  withInterceptors,
  withXhr,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from "@angular/core";
import { provideRouter } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { CAAS_BASE_URL, TRUST_CMS_HTML } from "@claas/coremedia-components";
import {
  TranslateLoader,
  TranslateModule,
  TranslationObject,
} from "@ngx-translate/core";
import { OktaAuthModule } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { map } from "rxjs";
import { routes } from "./app.routes";
import { environment } from "../environments/environment";
import { withBreadcrumbTranslations } from "./domains/coremedia/navigation/util/breadcrumb-translations";
import { CustomOverlayContainer } from "./domains/pia/core/providers/custom-overlay-container";
import { errorInterceptor } from "./domains/pia/shared/interceptors/error.interceptor";
import { loadingInterceptor } from "./domains/pia/shared/interceptors/loading.interceptor";
import { BASE_PATH } from "./domains/pia/shared/pia-api/generated/variables";
import { authTokenInterceptor } from "./shared/auth-token.interceptor";
import { GlobalErrorHandler } from "./shared/error/global-error-handler";
import { httpErrorInterceptor } from "./shared/error/http-error.interceptor";
import { mockApiInterceptor } from "./shared/mock-api.interceptor";
import { previewInterceptor } from "./shared/preview.interceptor";
import {
  AppConfig,
  RuntimeConfigService,
} from "./shared/runtime-config.service";

const oktaAuth = new OktaAuth({
  issuer: environment.okta.issuer,
  clientId: environment.okta.clientId,
  redirectUri: `${window.location.origin}/security/okta/login`,
  scopes: ["openid", "profile", "email"],
  pkce: true,
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withXhr(),
      withInterceptors([
        ...(environment.production ? [] : [mockApiInterceptor]),
        errorInterceptor,
        loadingInterceptor,
        httpErrorInterceptor,
        authTokenInterceptor,
        previewInterceptor,
      ]),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppConfig,
      deps: [RuntimeConfigService],
      multi: true,
    },
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: CAAS_BASE_URL, useValue: environment.caasPreviewHost },
    // FE-24: opt into the library's bypassSecurityTrustHtml for CoreMedia rich text.
    // Justified — CoreMedia content is authored by trusted internal staff, never
    // user-submitted. Full review: @claas/coremedia-components docs/security/SANITIZATION_REVIEW.md
    { provide: TRUST_CMS_HTML, useValue: true },
    provideRouter(routes),
    provideZonelessChangeDetection(),
    importProvidersFrom([
      OktaAuthModule.forRoot({ oktaAuth }),
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLanguage,
        loader: {
          provide: TranslateLoader,
          useFactory: (httpBackend: HttpBackend): TranslateLoader => {
            const loader = new MultiTranslateHttpLoader(httpBackend, [
              { prefix: "./assets/i18n/global/", suffix: ".json" },
              { prefix: "./assets/i18n/pia/", suffix: ".json" },
            ]);
            return {
              getTranslation: (language: string) =>
                loader
                  .getTranslation(language)
                  .pipe(
                    map((translations: TranslationObject) =>
                      withBreadcrumbTranslations(translations, language),
                    ),
                  ),
            };
          },
          deps: [HttpBackend],
        },
      }),
    ]),
    { provide: BASE_PATH, useValue: environment.pia.apiOrigin },
    // PIA modal dialogs render into the .pia-overlay element in the app shell.
    {
      provide: OverlayContainer,
      useFactory: () => new CustomOverlayContainer(".pia-overlay"),
    },
    provideServiceWorker("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
};

function loadAppConfig(runtimeConfig: RuntimeConfigService) {
  return async () => {
    const response = await fetch("assets/app-config.json");
    const cfg: AppConfig = await response.json();
    runtimeConfig.load(cfg);
  };
}
